import React, { useState, useRef, useCallback } from 'react';
import styles from './MelodySliders.module.css';

interface CustomSliderProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  marks?: Array<{ value: number; label: string }>;
  label: string;
  description?: string;
}

const CustomSlider: React.FC<CustomSliderProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  marks = [],
  label,
  description
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const getPercentage = useCallback((val: number) => {
    return ((val - min) / (max - min)) * 100;
  }, [min, max]);

  const getValueFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current) return value;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const rawValue = min + percentage * (max - min);
    
    // Snap to step
    const steppedValue = Math.round(rawValue / step) * step;
    return Math.max(min, Math.min(max, steppedValue));
  }, [min, max, step, value]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    const newValue = getValueFromPosition(e.clientX);
    onChange(newValue);
  }, [getValueFromPosition, onChange]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    const newValue = getValueFromPosition(e.clientX);
    onChange(newValue);
  }, [isDragging, getValueFromPosition, onChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const percentage = getPercentage(value);

  return (
    <div className={styles.sliderSection}>
      <div className={styles.sliderHeader}>
        <label className={styles.sliderLabel}>{label}</label>
        {description && (
          <span className={styles.sliderValue}>{description}</span>
        )}
      </div>
      <div className={styles.sliderWrapper}>
        <div 
          ref={sliderRef}
          className={styles.customSlider}
          onMouseDown={handleMouseDown}
        >
          <div 
            className={styles.sliderTrack}
            style={{ width: `${percentage}%` }}
          />
          <div 
            className={styles.sliderThumb}
            style={{ left: `${percentage}%` }}
          />
        </div>
        {marks.length > 0 && (
          <div className={styles.sliderMarks}>
            {marks.map((mark, index) => (
              <div key={index} className={styles.sliderMark}>
                {mark.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSlider;